/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import * as React from 'react';
import { Provider } from 'react-redux';
import 'sanitize.css/sanitize.css';
import './assets/css/globals.css';

// Import root app
import { App } from 'app';

import { HelmetProvider } from 'react-helmet-async';

import { configureAppStore } from 'store/configureStore';

// Initialize languages
import './locales/i18n';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { setupListeners } from '@reduxjs/toolkit/query';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import fr from 'date-fns/locale/fr';
import * as Sentry from '@sentry/react';

// Create redux store with history
export const store = configureAppStore();

setupListeners(store.dispatch);

// Initialize background services after authentication
import { backgroundInstructionsService } from 'services/BackgroundInstructionsService';

// Delay initialization to ensure the app is ready
setTimeout(() => {
  backgroundInstructionsService.initialize(store).catch(error => {
    console.error('Failed to initialize background instructions service:', error);
  });
}, 2000);

export type AppRootState = ReturnType<typeof store.getState>;
type RootContainer = HTMLElement & { __appRoot?: Root };

const MOUNT_NODE = document.getElementById('root') as RootContainer | null;

if (!MOUNT_NODE) {
  throw new Error('Application root element not found');
}

interface Props {
  Component: typeof App;
}

if (
  import.meta.env.NODE_ENV === 'production' &&
  import.meta.env.VITE_SENTRY_URL
) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_URL,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const ConnectedApp = ({ Component }: Props) => {
  const isElectron = typeof window !== 'undefined' && Boolean(window.platform?.isElectron);
  const Router: any = isElectron ? HashRouter : BrowserRouter;
  const ReduxProvider = Provider as unknown as React.ComponentType<any>;
  return (
  <ReduxProvider store={store}>
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <Router>
          <HelmetProvider>
            <Component />
          </HelmetProvider>
        </Router>
      </LocalizationProvider>
    </ErrorBoundary>
  </ReduxProvider>
)};

const root = MOUNT_NODE.__appRoot ?? createRoot(MOUNT_NODE);
MOUNT_NODE.__appRoot = root;

root.render(<ConnectedApp Component={App} />);
